import React, { useState, useEffect, ReactElement } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import {
    CompositeFilterDescriptor,
    filterBy,
    FilterDescriptor,
    FilterOperator,
    orderBy,
} from '@progress/kendo-data-query';
import {
    ListItemProps,
    MultiSelectChangeEvent,
    MultiSelectFilterChangeEvent,
    TagData,
} from '@progress/kendo-react-dropdowns';
import styles from './style.module.scss';
import { MultiSelect, MultiSelectProps } from '@components-clean/core/multi-select';
import Tooltip from '@components-clean/core/tooltip';
import { useTranslation } from '@hooks/index';

export type Person = {
    email: string;
    firstName: string;
    lastName: string;
};

interface MultiSelectControllerProps<TData, TControl extends FieldValues> extends Props {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean>;
    handleInputChange?: (event: string) => void;
}

interface Props extends Omit<MultiSelectProps<Person>, 'value' | 'onChange' | 'isCountryMultiSelect'> {
    onChange?: (value: string) => void;
}

const defaultFilter: CompositeFilterDescriptor = {
    logic: 'or',
    filters: [{ operator: FilterOperator.IsNotEmpty, value: '' }],
};

const validateEmail = (email: string) => {
    const emailReg = /^[\W]*[\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*$/;

    return emailReg.test(email.toLowerCase());
};

const getEmailList = (recipients: Person[]) => recipients.map((person: Person) => person.email).join(';');

export const MultiSelectEmail = ({ data, onChange, ...rest }: Props) => {
    const [people, setPeople] = useState<Person[]>([]);
    const [filteredData, setFilteredData] = useState<Person[]>([]);
    const [filter, setFilter] = useState<CompositeFilterDescriptor>(defaultFilter);
    const [value, setValue] = useState<Person[]>([]);

    const getFilterValue = () => (filter.filters[0] as FilterDescriptor).value as string;

    const calculateEmailsFromFreeText = (text: string) => {
        const entries = text.split(';').map((entry: string) => entry.trim());

        const peopleToAdd: Person[] = [];
        const remainingEntries = [...entries];
        const updatedData = [...people];

        entries.forEach((entry: string) => {
            const lowerCaseEntry = entry.toLowerCase();

            const removeFromRemainingEmails = () => {
                remainingEntries.splice(remainingEntries.indexOf(entry), 1);
            };

            const existingPersonIndex = people.findIndex(
                (person: Person) =>
                    person.email.toLowerCase() === lowerCaseEntry ||
                    `${person.firstName} ${person.lastName}`.toLowerCase() === lowerCaseEntry,
            );

            const shouldAddMemberEmail =
                existingPersonIndex > -1 && !peopleToAdd.some((item: Person) => item.email === lowerCaseEntry);

            if (shouldAddMemberEmail) {
                peopleToAdd.push(people[existingPersonIndex]);
                updatedData.splice(existingPersonIndex, 1);
                removeFromRemainingEmails();
                return;
            }

            const shouldAddNonMemberEmail =
                validateEmail(lowerCaseEntry) &&
                !value.some((item: Person) => item.email === lowerCaseEntry) &&
                !peopleToAdd.some((item: Person) => item.email === lowerCaseEntry);

            if (shouldAddNonMemberEmail) {
                peopleToAdd.push({
                    email: lowerCaseEntry,
                    firstName: '',
                    lastName: '',
                });
                removeFromRemainingEmails();
            }
        });

        return {
            peopleToAdd,
            remainingEntries,
            updatedData,
        };
    };

    const addEmailsFromFreeText = (text: string) => {
        const { peopleToAdd, remainingEntries, updatedData } = calculateEmailsFromFreeText(text);

        const updatedFilter = {
            ...filter,
            filters: (filter.filters as FilterDescriptor[]).map((filterDescriptor: FilterDescriptor) => ({
                ...filterDescriptor,
                value: remainingEntries.join(';'),
            })),
        };

        setFilter(updatedFilter);
        const newValue = [...value, ...peopleToAdd];
        onChange?.(getEmailList(newValue));
        setValue(newValue);
        setPeople(updatedData);
    };

    const updateData = (updatedValue: Person[]) => {
        const updatedValueIds = updatedValue.map((valueItem: Person) => valueItem.email);
        const newData = data.filter((dataItem: Person) => !updatedValueIds.includes(dataItem.email));

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setPeople(newData);
    };

    const updateFilteredData = (dataToFilter: Person[], updatedFilter: CompositeFilterDescriptor) => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setFilteredData(orderBy(filterBy(dataToFilter.slice(), updatedFilter), [{ field: 'firstName', dir: 'asc' }]));
    };

    useEffect(() => {
        if (data) updateData(value);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (people) updateFilteredData(people, filter);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [people]);

    const filterChange = (event: MultiSelectFilterChangeEvent) => {
        const spaceSeparatedValues = (event.filter.value as string)
            .split(' ')
            .filter((separatedValue: string) => separatedValue !== '');

        const invalidNameValues = [...spaceSeparatedValues];
        invalidNameValues.splice(0, 1);
        let invalidNameValue = invalidNameValues.join(' ');

        if (invalidNameValue === spaceSeparatedValues[1]) invalidNameValue = '';

        const fullNameFilter: CompositeFilterDescriptor = {
            logic: 'and',
            filters: [
                {
                    field: 'firstName',
                    operator: FilterOperator.EqualTo,
                    value: spaceSeparatedValues[0],
                },
                {
                    field: 'lastName',
                    operator: FilterOperator.StartsWith,
                    value: spaceSeparatedValues[1] ?? '',
                },
                {
                    field: 'lastName',
                    operator: FilterOperator.Contains,
                    value: invalidNameValue,
                },
            ],
        };

        const updatedFilter = {
            ...filter,
            filters: [
                { ...event.filter, field: 'email' },
                { ...event.filter, field: 'firstName' },
                { ...event.filter, field: 'lastName' },
                fullNameFilter,
            ],
        };

        updateFilteredData(people, updatedFilter);
        setFilter(updatedFilter);
    };

    const onBlur = () => {
        addEmailsFromFreeText(getFilterValue());
    };

    const itemRender = (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const dataItem = itemProps.dataItem as Person;

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-clone-element
        return React.cloneElement(li, li.props, [
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/no-missing-key
            <span className="k-d-flex k-justify-content-evenly k-gap-1">
                <span>
                    {dataItem.firstName} {dataItem.lastName}
                </span>
                <span>&lt;{dataItem.email}&gt;</span>
            </span>,
        ]);
    };

    const tagRender = (tagData: TagData, li: React.ReactElement<HTMLLIElement>) => {
        const person = tagData.data[0] as Person;

        const displayText = () => {
            const text = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

            return text || person?.email || '';
        };

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-clone-element
        return React.cloneElement(li, li.props, [
            <span key={person?.email || ''} title={person?.email} className={styles.tag}>
                <Tooltip parentTitle position="top">
                    {displayText()}
                </Tooltip>
            </span>,
        ]);
    };

    return (
        <MultiSelect
            isEditing
            {...rest}
            onBlur={onBlur}
            data={filteredData}
            useDefaultCustomValueHandler={false}
            onChange={(event: MultiSelectChangeEvent) => {
                const values = event.target.value as Array<Person | string>;

                const lastItem = values[values.length - 1];
                if (typeof lastItem === 'string') {
                    addEmailsFromFreeText(lastItem);
                } else {
                    const newValues = values as Person[];
                    onChange?.(getEmailList(newValues));
                    setValue(newValues);
                    updateData(newValues);
                }
            }}
            value={value}
            filterable
            filter={getFilterValue()}
            onFilterChange={filterChange}
            itemRender={itemRender}
            tags={value.map((item) => ({ text: item.email, data: [item] }))}
            tagRender={tagRender}
        />
    );
};

export const MultiSelectEmailController = <TData, TControl extends FieldValues>({
    control,
    name,
    validateRules,
    handleInputChange,
    required = false,
    ...props
}: MultiSelectControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange }, fieldState: { error } }) => (
                <MultiSelectEmail
                    {...props}
                    valid={!error}
                    onChange={(event) => {
                        onChange(event);
                        if (handleInputChange) handleInputChange(event);
                    }}
                />
            )}
        />
    );
};
